import * as React from "react";
import {useMemo} from "react";
import {useAuth} from "@/context/AuthProvider";
import {Navigate, useLocation} from "react-router";
import {PredefinedContextName, PredefinedRoleName} from "../predefinedAuthNames";
import {RoleDomainNameQuery, useRoleDomainNameQuery} from "./queries.generated";
import {PersonalExtensionListQuery, usePersonalExtensionListQuery} from "@/queries.generated";
import RedirectToError from "./RedirectToError";
import {path as errorPagePath} from "../errorPageUrl";

/**
 * Determinates and extracts the relevant ID for redirection based on the shape of the query data.
 *
 * @param data The data returned from either RoleDomainNameQuery or PersonalExtensionListQuery
 * @returns An object containing the type of entity ("domain" or "extension") and its corresponding ID, or undefined if missing necessary information.
 */
const getIdFromQueryData = (
    data: RoleDomainNameQuery | PersonalExtensionListQuery | undefined,
): {type: "domain" | "extension"; id: number} | undefined => {
    if (!data) return undefined;
    if ("domains" in data) {
        return {
            type: "domain",
            id: data.domains.items[0]?.id,
        };
    } else if ("personalUsers" in data) {
        return {
            type: "extension",
            id: data.personalUsers[0]?.id,
        };
    } else {
        console.error("getIdFromQueryData: Unrecognized query data shape", data);
        return undefined;
    }
};

export default function RoleRedirect(props: {children: React.ReactNode}) {
    const auth = useAuth();
    const location = useLocation();

    const isRootPath = location.pathname === "/";

    // Derive role-based intent to reduce recalculation noise
    const intent = useMemo(() => {
        const domainNames: string[] = [];
        const extensionIdentifiers: string[] = [];

        let hasSuperuser = false;
        let hasReporter = false;
        let hasImplementor = false;

        for (const role of auth.roles) {
            if (role.name === PredefinedRoleName.superuser) hasSuperuser = true;
            if (role.name === PredefinedRoleName.reporter) hasReporter = true;
            if (role.name === PredefinedRoleName.implementor) hasImplementor = true;
            for (const ctx of role.contexts) {
                if (ctx.name === PredefinedContextName.domain && ctx.data?.name) {
                    domainNames.push(ctx.data.name);
                }
                if (ctx.name === PredefinedContextName.extension && ctx.data?.identifier) {
                    extensionIdentifiers.push(ctx.data.identifier);
                }
            }
        }
        const roleCount = auth.roles.length;
        const needsAsync =
            (domainNames.length === 1 && extensionIdentifiers.length === 0) ||
            (roleCount === 1 && extensionIdentifiers.length === 1);

        return {domainNames, extensionIdentifiers, hasReporter, hasImplementor, hasSuperuser, roleCount, needsAsync};
    }, [auth.roles]);

    // Determinate which query to run based on the intent, if needed any
    const queryScenario = useMemo(() => {
        if (!isRootPath) return {type: "none"};
        if (!intent.needsAsync) return {type: "none"};
        if (intent.domainNames.length === 1 && intent.extensionIdentifiers.length === 0)
            return {
                type: "domain",
                name: intent.domainNames[0],
            };
        if (intent.roleCount === 1 && intent.extensionIdentifiers.length === 1)
            return {
                type: "extension",
                identifier: intent.extensionIdentifiers[0],
            };
        return {type: "none"};
    }, [isRootPath, intent.needsAsync, intent.domainNames, intent.extensionIdentifiers, intent.roleCount]);

    // If the scenario requires async resolution, run the appropriate query. Otherwise, skip querying.
    const query =
        queryScenario.type === "domain"
            ? useRoleDomainNameQuery({
                  variables: {name: intent.domainNames[0]},
              })
            : usePersonalExtensionListQuery({
                  skip: queryScenario.type !== "extension",
              });

    // Extract the relevant ID for redirection from the query data, if available
    const queryData = getIdFromQueryData(query.data);

    if (auth.roles.length === 0 && location.pathname !== errorPagePath)
        return <RedirectToError code={403} id="no_roles" />;

    // While waiting for async resolution on root, avoid rendering children
    if (isRootPath && query.loading) return null;

    if (isRootPath) {
        if (queryData) {
            const target =
                queryData.type === "domain"
                    ? `/domains/${queryData.id}`
                    : `/personal-extensions/${queryData.id}/profiles`;
            if (location.pathname !== target) {
                return <Navigate to={target} replace />;
            }
        }

        if (intent.hasSuperuser) {
            // do nothing, superuser can stay on the dashboard
        } else if (intent.hasReporter) {
            if (location.pathname !== "/endpoint-status") {
                return <Navigate to="/endpoint-status" replace />;
            }
        } else if (intent.hasImplementor) {
            if (location.pathname !== "/mac-address-management") {
                return <Navigate to="/mac-address-management" replace />;
            }
        } else if (
            intent.domainNames.length > 1 ||
            (intent.domainNames.length === 1 && intent.extensionIdentifiers.length > 0)
        ) {
            if (location.pathname !== "/domains") {
                return <Navigate to="/domains" replace />;
            }
        }
        // Multiple personal extensions without any domain access -> go to personal extensions list
        else if (intent.domainNames.length === 0 && intent.extensionIdentifiers.length > 1) {
            if (location.pathname !== "/personal-extensions") {
                return <Navigate to="/personal-extensions" replace />;
            }
        }
    }

    // Navigation is handled imperatively in effects.

    return <React.Fragment>{props.children}</React.Fragment>;
}
